import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Checkbox,
  Col,
  Form,
  Input,
  Layout,
  message,
  Row,
  Select,
  TimePicker,
} from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { omit } from 'lodash';

import AddressServices from 'services/address';
import LocationServices from 'services/location';
import WarehouseServices from 'services/warehouse';

import CoordinatePicker from 'components/coordinatePicker';
import { PageSpinner } from 'components/page-spinner';
import PdButton from 'components/pd-button';

import { errHandling } from 'utils';

import './style.scss';

dayjs.extend(customParseFormat);

const { TextArea } = Input;
const { Content } = Layout;
const { Option } = Select;

const TIME_FORMAT = 'HH:mm';
const DEFAULT_OPERATING_HOURS = [
  dayjs('09:00', TIME_FORMAT),
  dayjs('15:30', TIME_FORMAT),
];
const HOUR_FORMATS = ['HH:mm', 'HH:mm:ss'];
const TIMEZONES = ['WIB', 'WITA', 'WIT'];

const FIELDS = {
  CITY: 'city_id',
  FIRST_LINE_ADDRESS: 'address',
  ID: 'acumatica_id',
  JNE_CODE: 'jne_origin_code',
  NAME: 'title',
  PINPOINT: 'pinpoint',
  POSTALCODE: 'zip_code',
  PROVINCE: 'province_id',
  SECOND_LINE_ADDRESS: 'second_line_address',
  STORE_PAYMENT: 'store_payment',
  LOCATION: 'location_id',
  OPEN_HOUR: 'open_hour',
  CLOSE_HOUR: 'close_hour',
  TIMEZONE: 'timezone',
  OPERATING_HOURS: 'operating_hours',
};

function parseHour(hour) {
  if (!hour) return undefined;
  if (dayjs.isDayjs(hour)) return hour;
  const timePart = String(hour).match(/(\d{1,2}:\d{2}(?::\d{2})?)/);
  if (timePart) {
    const parsed = dayjs(timePart[1], HOUR_FORMATS);
    if (parsed.isValid()) return parsed;
  }
  const fallback = dayjs(hour);
  return fallback.isValid() ? fallback : undefined;
}

function formatHour(hour) {
  const parsed = parseHour(hour);
  return parsed ? parsed.format(TIME_FORMAT) : undefined;
}

function timezoneFromOffset(offset) {
  if (offset === null || offset === undefined || offset === '') return undefined;
  const hours = Math.abs(Number(offset)) > 24 ? Math.round(Number(offset) / 60) : Number(offset);
  return { 7: 'WIB', 8: 'WITA', 9: 'WIT' }[hours];
}

export function WarehouseCreateEdit() {
  const [form] = Form.useForm();
  const [formLayout] = useState('vertical');
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [provinceList, setProvinceList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [value, setValue] = useState({});
  const [mapCoordinate, setMapCoordinate] = useState({});
  const [locations, setLocations] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');

  const formItemLayout =
    formLayout === 'horizontal'
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 14 },
        }
      : null;

  const buttonItemLayout =
    formLayout === 'horizontal'
      ? {
          wrapperCol: { span: 14, offset: 4 },
        }
      : null;

  async function onFinish(values) {
    setLoadingButton(true);
    try {
      const { lat = -6.914744, lng = 107.60981 } = mapCoordinate;
      const operatingHours = values[FIELDS.OPERATING_HOURS];
      const [openHour, closeHour] = operatingHours || [];
      const payload = {
        warehouse: {
          ...omit(values, [FIELDS.OPERATING_HOURS, FIELDS.TIMEZONE]),
          [FIELDS.OPEN_HOUR]: formatHour(openHour),
          [FIELDS.CLOSE_HOUR]: formatHour(closeHour),
          lat,
          lng,
          store_payment: value[FIELDS.STORE_PAYMENT],
        },
      };
      if (id) {
        await WarehouseServices.updateWarehouse(id, payload);
        message.success('Successfully update the warehouse');
      } else {
        await WarehouseServices.createWarehouse(payload);
        message.success('Successfully add a warehouse');
      }
      navigate('/warehouses');
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingButton(false);
    }
  }

  function changeValue(key, e) {
    if (e) {
      setValue((prevState) => ({
        ...prevState,
        [key]: e,
      }));
    }
  }

  function handleOnChangeProvince(value) {
    fetchCities(value);
    changeValue(FIELDS.PROVINCE, value);
  }

  async function fetchCities(provinceId) {
    try {
      const {
        data: { data = [] },
      } = await AddressServices.getCityList(provinceId);
      setCityList(data);
    } catch (error) {
      errHandling(error);
    }
  }

  async function fetchProvinces() {
    setLoadingPage(true);
    try {
      const {
        data: { data = [] },
      } = await AddressServices.getProvinceList();
      setProvinceList(data);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingPage(false);
    }
  }

  async function fetchWarehouseDetail(warehouseId) {
    try {
      const {
        data: { data = {} },
      } = await WarehouseServices.getWarehouseDetail(warehouseId);
      const {
        province_id = '',
        lat,
        lng,
        address = '',
        open_hour,
        close_hour,
        timezone,
      } = data;
      setMapCoordinate({ address, lat, lng });
      fetchCities(province_id);
      setValue({
        ...data,
        location_id: data.location?.id,
        [FIELDS.STORE_PAYMENT]: data.store_payment,
      });
      const openHourValue = parseHour(open_hour);
      const closeHourValue = parseHour(close_hour);
      form.setFieldsValue({
        [FIELDS.LOCATION]: data.location?.id,
        [FIELDS.NAME]: data.title,
        [FIELDS.ID]: data.acumatica_id,
        [FIELDS.FIRST_LINE_ADDRESS]: data.address,
        [FIELDS.SECOND_LINE_ADDRESS]: data.second_line_address,
        [FIELDS.PROVINCE]: province_id ? String(province_id) : undefined,
        [FIELDS.CITY]: data.city_id ? String(data.city_id) : undefined,
        [FIELDS.POSTALCODE]: data.zip_code,
        [FIELDS.JNE_CODE]: data.jne_origin_code,
        [FIELDS.OPERATING_HOURS]:
          openHourValue || closeHourValue
            ? [openHourValue, closeHourValue]
            : undefined,
        [FIELDS.TIMEZONE]: TIMEZONES.includes(timezone)
          ? timezone
          : timezoneFromOffset(data.utc_offset),
      });
    } catch (error) {
      errHandling(error);
    }
  }

  async function fetchLocations() {
    try {
      const {
        data: { data = [] },
      } = await LocationServices.getLocations();
      setLocations(data);
    } catch (error) {
      errHandling(error);
    }
  }

  useEffect(() => {
    fetchProvinces();
    fetchLocations();
    if (id) {
      fetchWarehouseDetail(id);
    }
    // eslint-disable-next-line
  }, []);

  if (loadingPage) {
    return <PageSpinner />;
  }

  return (
    <Layout className="pd-cms-products main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">
            {id ? 'Edit' : 'Create New'} Warehouse
          </div>
          <Content>
            <Form
              form={form}
              layout={formLayout}
              initialValues={{ [FIELDS.OPERATING_HOURS]: DEFAULT_OPERATING_HOURS }}
              onFinish={onFinish}>
              <div className="section-container">
                <div className="inner-section">
                  <Form.Item
                    label="Location"
                    name={FIELDS.LOCATION}
                    {...formItemLayout}
                    rules={[
                      {
                        required: true,
                        message: 'Please input location!',
                      },
                    ]}>
                    <Select
                      onChange={changeValue.bind(this, FIELDS.LOCATION)}
                      placeholder="Choose location">
                      {locations.map(({ id, title }) => (
                        <Option key={id} value={id}>
                          {title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Warehouse Name"
                    name={FIELDS.NAME}
                    {...formItemLayout}
                    rules={[
                      {
                        message: 'Please input Warehouse Name!',
                        required: true,
                      },
                    ]}>
                    <Input
                      placeholder="Write warehouse name here"
                      onChange={changeValue.bind(this, FIELDS.NAME)}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Acumatica ID"
                    name={FIELDS.ID}
                    {...formItemLayout}
                    rules={[
                      {
                        message: 'Please input Acumatica Id!',
                        required: true,
                      },
                    ]}>
                    <Input
                      placeholder="Write acumatica id here"
                      onChange={changeValue.bind(this, FIELDS.ID)}
                    />
                  </Form.Item>

                  <CoordinatePicker
                    mapCoordinate={mapCoordinate}
                    mapCoordinateSetter={setMapCoordinate}
                  />

                  <Form.Item
                    label="First Line Address"
                    name={FIELDS.FIRST_LINE_ADDRESS}
                    {...formItemLayout}
                    rules={[
                      {
                        required: true,
                        message: 'Please input address!',
                      },
                    ]}>
                    <TextArea
                      onChange={changeValue.bind(
                        this,
                        FIELDS.FIRST_LINE_ADDRESS
                      )}
                      placeholder="Write warehouse first line address here"
                      rows={5}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Second Line Address"
                    name={FIELDS.SECOND_LINE_ADDRESS}
                    {...formItemLayout}>
                    <TextArea
                      onChange={changeValue.bind(
                        this,
                        FIELDS.SECOND_LINE_ADDRESS
                      )}
                      placeholder="Write warehouse second line address here"
                      rows={5}
                    />
                  </Form.Item>

                  <Row gutter={20}>
                    <Col span={12}>
                      <Form.Item
                        label="Province"
                        name={FIELDS.PROVINCE}
                        {...formItemLayout}
                        rules={[
                          {
                            required: true,
                            message: 'Please input province!',
                          },
                        ]}>
                        <Select
                          onChange={handleOnChangeProvince}
                          placeholder="Choose warehouse province">
                          {provinceList.map(
                            ({ province_id = '', province = '' }) => (
                              <Option key={province_id} value={province_id}>
                                {province}
                              </Option>
                            )
                          )}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="City"
                        name={FIELDS.CITY}
                        {...formItemLayout}
                        rules={[
                          {
                            required: true,
                            message: 'Please input city!',
                          },
                        ]}>
                        <Select
                          onChange={changeValue.bind(this, FIELDS.CITY)}
                          placeholder="Choose warehouse city">
                          {cityList.map(({ city_id = '', city = '' }) => (
                            <Option key={city_id} value={city_id}>
                              {city}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Postal Code"
                    name={FIELDS.POSTALCODE}
                    {...formItemLayout}
                    rules={[
                      {
                        message: 'Please input postal code!',
                        required: true,
                      },
                    ]}>
                    <Input
                      placeholder="Write warehouse postal code here"
                      onChange={changeValue.bind(this, FIELDS.POSTALCODE)}
                    />
                  </Form.Item>

                  <Form.Item
                    label="JNE Code"
                    name={FIELDS.JNE_CODE}
                    {...formItemLayout}
                    rules={[
                      {
                        message: 'Please input JNE code!',
                        required: true,
                      },
                    ]}>
                    <Input
                      placeholder="Write warehouse JNE code here"
                      onChange={changeValue.bind(this, FIELDS.JNE_CODE)}
                    />
                  </Form.Item>
                  <Row gutter={20}>
                    <Col span={12}>
                      <Form.Item
                        label="Open/Close Hour"
                        name={FIELDS.OPERATING_HOURS}
                        {...formItemLayout}
                        rules={[
                          {
                            message: 'Please input open and close hour!',
                            required: true,
                          },
                        ]}>
                        <TimePicker.RangePicker
                          format={TIME_FORMAT}
                          placeholder={['Open hour', 'Close hour']}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Timezone"
                        name={FIELDS.TIMEZONE}
                        {...formItemLayout}>
                        <Select
                          disabled
                          placeholder="Set automatically from warehouse location">
                          {TIMEZONES.map((tz) => (
                            <Option key={tz} value={tz}>
                              {tz}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item label="" {...formItemLayout}>
                    <Checkbox
                      checked={value[FIELDS.STORE_PAYMENT]}
                      onChange={(e) => {
                        setValue((prev) => ({
                          ...prev,
                          [FIELDS.STORE_PAYMENT]: e.target.checked,
                        }));
                      }}>
                      This warehouse support store payment
                    </Checkbox>
                  </Form.Item>
                </div>
              </div>

              <div className="button-container">
                <Form.Item {...buttonItemLayout}>
                  <div className="buttonSection mt-40">
                    <PdButton
                      full
                      htmlType="submit"
                      loading={loadingButton}
                      type="primary">
                      Publish
                    </PdButton>
                  </div>
                </Form.Item>
              </div>
            </Form>
          </Content>
        </div>
      </div>
    </Layout>
  );
}

export default WarehouseCreateEdit;
